// The laptop's Pocket System host. One hidden iframe realm per package (the
// Pocket Shell Desktop System UI plus each Studio app that has a window), a
// fixed-step scheduler, child raster upload into the shell compositor and a 2D
// canvas the room uses as the laptop screen texture.
//
// The host registers no DOM listeners. The page calls the input methods with
// desktop logical pixels and key fields, so the canvas can stay detached.
//
// The theme has one owner, the System UI. The host sets it with the System
// UI's theme line and learns it from the line the System UI sends back, which
// also reports a change made in the Start menu or with the cycle chord. Each
// report is added to the state line the apps receive and is kept in
// localStorage for the viewer's next visit.

import { langOf, type Lang } from "../../desktop/apps/shared/words.ts";
import type { DesktopThemeId, StudioIntentLine, StudioStateLine } from "../core/protocol.ts";
import {
  DEFAULT_THEME,
  KEY_BUTTONS,
  STUDIO_SERVICE,
  frameAt,
  langLine,
  lineFor,
  packTouchCancel,
  packTouchWide,
  parseIntent,
  queueLaunch,
  readStoredTheme,
  reportedTheme,
  routeKey,
  storeTheme,
  toClient,
  type DeskLine,
  type KeyLike,
  type ShellLine,
  type ShowRequest,
  type SurfaceFrame,
  type ThemeStore,
} from "./desk-input.ts";

export type { KeyLike } from "./desk-input.ts";

export type DesktopRate = "full" | "idle" | "paused";
export type DesktopTheme = DesktopThemeId;

export interface DesktopWindow {
  /** Package id of the app the window shows. */
  package: string;
  /** Client rectangle [x, y, width, height]. */
  rect: readonly [number, number, number, number];
  focused: boolean;
}

export interface StudioDesktop {
  /** 2D canvas the host paints the composited desktop into. */
  readonly canvas: HTMLCanvasElement;
  /** Desktop size in logical pixels; the canvas has the same pixel size. */
  readonly size: readonly [number, number];
  /**
   * Move the pointer and report a button. `down` is the state of `button`
   * (1 primary, 2 secondary); pass the primary state on plain moves.
   */
  pointer(x: number, y: number, down: boolean, button?: 1 | 2, shift?: boolean): void;
  /** Wheel travel as DOM deltaY in pixels. */
  wheel(deltaY: number): void;
  /** Returns true when the desktop consumed the key. */
  keyDown(event: KeyLike): boolean;
  keyUp(event: KeyLike): void;
  /** Release held buttons and the pointer. */
  blur(): void;
  /**
   * Send the state to every Studio app; apps that start later get the latest
   * line. The host adds the active theme to the line.
   */
  publish(line: StudioStateLine): void;
  /**
   * Called with each intent an app sends. The host answers a theme intent
   * itself, by selecting that theme; listeners see it as well.
   */
  onIntent(listener: (intent: StudioIntentLine) => void): () => void;
  /** The active theme, as the System UI last reported it. */
  theme(): DesktopTheme;
  /**
   * Select a theme. The System UI switches at its next step and reports
   * back; the apps redraw and the choice is stored when that report arrives.
   * A paused desktop holds the last theme asked for until the rate leaves
   * "paused".
   */
  setTheme(theme: DesktopTheme): void;
  /** Called when the active theme changes, whoever changed it. */
  onTheme(listener: (theme: DesktopTheme) => void): () => void;
  /** Called after each paint that changed canvas pixels. */
  onFrame(listener: () => void): () => void;
  /** Called with a CSS cursor keyword when the pointer shape changes. */
  onCursor(listener: (cursor: string) => void): () => void;
  /** Called when the user confirms Shut Down in the System UI. */
  onQuit(listener: () => void): () => void;
  /** Studio app windows on screen, bottom to top, with their client rectangles in desktop pixels. */
  windows(): readonly DesktopWindow[];
  /**
   * Open an installed app by package id, as a double-click on its desktop icon
   * does. When the app's window is already open, the System UI restores it if
   * it is minimized, raises it and focuses it; no second window opens. A
   * package id that is not an installed app is logged and ignored.
   *
   * The System UI reads the request at its next step: the next frame at
   * "full", the next wake at "idle". A paused desktop holds the request, one
   * per app, and sends it when the rate leaves "paused"; the app asked for
   * last ends up focused.
   *
   * `show` names a section of the app to show, such as a device of the
   * Install Guide. The app receives it as the `show` of its state lines,
   * counted, until its window closes: an app that starts later reads it from
   * its first line, and one already open from its next.
   */
  launch(packageId: string, show?: string): void;
  /**
   * "full" steps at 60 Hz. "idle" wakes 5 times a second, replays the elapsed
   * steps and paints once. "paused" stops stepping and ignores input.
   */
  setRate(rate: DesktopRate): void;
  stop(): void;
}

export interface MountStudioDesktopOptions {
  /** Created detached when omitted. */
  canvas?: HTMLCanvasElement;
  /** Where scripts/build-desktop.ts output is served, for example "/desktop/". */
  baseUrl: string;
  /** Theme at boot. Default: the theme the viewer chose last, or "aqua". */
  theme?: DesktopTheme;
  /** Keep the System UI's welcome note open at boot. Default false. */
  welcome?: boolean;
  onLog?: (message: string) => void;
}

// ---- realm contract (vendor/pocketjs/hosts/web/app-instance.js) -------------

interface RealmApi {
  readonly packageId: string;
  readonly viewport: [number, number];
  step(buttons?: number, touches?: number[], hits?: number[], touchSurfaces?: number[]): void;
  hitTestBounds(x: number, y: number): number;
  render(): Uint8Array;
  renderComposited(): Uint8Array;
  drawHash(): bigint;
  bindings(): Array<{ handle: number; focused: boolean }>;
  frames(): SurfaceFrame[];
  uploadSurface(handle: number, pixels: Uint8Array, width: number, height: number): number;
  freeSurface(handle: number): void;
  sendService(line: string | object): void;
  drainService(): string[];
  dispose(): void;
}

interface RealmOptions {
  packageId: string;
  wasmUrl: string;
  bundleUrl: string;
  pakUrl: string;
  viewport: readonly [number, number];
  rasterDensity: number;
  companions: readonly string[];
  surfaces?: Record<string, number>;
}

interface Realm {
  iframe: HTMLIFrameElement;
  api: RealmApi;
}

interface PackagePlan {
  app: { id: string; output: string };
  target: { id: string; hostAbi: number };
  viewport: { logical: [number, number]; rasterDensity: number };
  features: Record<string, boolean>;
  companions: string[];
}

interface SystemPackage {
  package: string;
  plan: PackagePlan;
}

interface SystemPlan {
  system: { title: string };
  target: { id: string; hostAbi: number };
  roles: { systemUI: string };
  lifecycle: { backgroundExecution: "suspend" | "continue" };
  installation: { installedPackages: string[] };
  systemUI: SystemPackage;
  applications: SystemPackage[];
}

const TARGET = "web-app";
const HOST_ABI = 4;

function validatePlan(plan: SystemPlan): void {
  if (!plan || plan.target?.id !== TARGET || plan.target?.hostAbi !== HOST_ABI) {
    throw new Error(`the desktop host needs a ${TARGET} ABI ${HOST_ABI} system plan`);
  }
  if (!plan.systemUI || plan.roles?.systemUI !== plan.systemUI.package) {
    throw new Error("the System UI role does not match its package plan");
  }
  if (plan.systemUI.plan?.features?.["ui.compositor-surfaces"] !== true) {
    throw new Error("the System UI plan lacks ui.compositor-surfaces");
  }
  const ids = new Set<string>();
  const outputs = new Set<string>();
  for (const entry of [plan.systemUI, ...(plan.applications ?? [])]) {
    const output = entry.plan?.app?.output;
    if (ids.has(entry.package)) throw new Error(`duplicate package ${entry.package}`);
    if (!output || outputs.has(output)) throw new Error(`duplicate or missing artifact ${output}`);
    if (entry.plan.app.id !== entry.package) throw new Error(`package ${entry.package} carries another plan`);
    if (entry.plan.target?.id !== TARGET || entry.plan.target?.hostAbi !== HOST_ABI) {
      throw new Error(`package ${entry.package} was not resolved for ${TARGET}`);
    }
    ids.add(entry.package);
    outputs.add(output);
  }
  const installed = new Set(plan.installation?.installedPackages ?? []);
  if (installed.size !== ids.size || [...ids].some((id) => !installed.has(id))) {
    throw new Error("resolved packages do not match the installation snapshot");
  }
}

async function createRealm(instanceUrl: string, options: RealmOptions): Promise<Realm> {
  const iframe = document.createElement("iframe");
  iframe.hidden = true;
  iframe.tabIndex = -1;
  iframe.setAttribute("aria-hidden", "true");
  const loaded = new Promise<void>((resolve, reject) => {
    iframe.addEventListener("load", () => resolve(), { once: true });
    iframe.addEventListener("error", () => reject(new Error(`failed to load ${instanceUrl}`)), { once: true });
  });
  iframe.src = instanceUrl;
  document.body.appendChild(iframe);
  try {
    await loaded;
    const factory = (iframe.contentWindow as unknown as {
      PocketAppInstance?: { create(options: RealmOptions): Promise<RealmApi> };
    } | null)?.PocketAppInstance;
    if (!factory) throw new Error("the app realm did not publish PocketAppInstance");
    return { iframe, api: await factory.create(options) };
  } catch (error) {
    iframe.remove();
    throw error;
  }
}

/**
 * Record which shell node carries each surface handle, by wrapping the spec
 * op the System UI binds surfaces with. The realm's `ui` object is the op
 * table the framework calls through, so the wrapper sees every binding made
 * after boot. Returns null when the op is absent.
 */
function observeSurfaceNodes(realm: Realm): Map<number, number> | null {
  const ops = (realm.iframe.contentWindow as unknown as {
    ui?: { setCompositorSurface?: (id: number, handle: number, focused: number) => void };
  } | null)?.ui;
  const bind = ops?.setCompositorSurface;
  if (!ops || typeof bind !== "function") return null;
  const nodes = new Map<number, number>();
  ops.setCompositorSurface = (id, handle, focused) => {
    if (handle >= 0) nodes.set(handle, id);
    else for (const [known, node] of nodes) if (node === id) nodes.delete(known);
    bind(id, handle, focused);
  };
  return nodes;
}

const FRAME_MS = 1000 / 60;
/** Longest stretch of wall time one wake replays as fixed steps. */
const CATCH_UP_MS = 250;
const IDLE_INTERVAL_MS = 200;
/** Wait before starting an app again after its bundle or pack failed to load. */
const START_RETRY_MS = 3000;

/** The viewer's storage. Null when the browser denies access to it. */
function themeStore(): ThemeStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
const CURSORS: Readonly<Record<string, string>> = {
  default: "default",
  text: "text",
  pointer: "pointer",
  move: "move",
  grabbing: "grabbing",
  ew: "ew-resize",
  ns: "ns-resize",
  nwse: "nwse-resize",
  nesw: "nesw-resize",
};

interface Child {
  entry: SystemPackage;
  /** Null until the realm has booted. */
  realm: Realm | null;
  closed: boolean;
  /** The realm did not start, or the app threw in a frame. */
  failed: boolean;
  /** When a failed start is tried again; Infinity for an app that threw. */
  retryAt: number;
  /** The latest state line has not been delivered yet. */
  stale: boolean;
  /** Draw hash of the raster the shell compositor holds; null before the first upload. */
  hash: bigint | null;
  /** Node hit at the contact's down edge, carried until it lifts. */
  contactHit: number | null;
}

/** A primary press that landed on an app surface, fed to the app as touch contact 0. */
interface Press {
  handle: number;
  /** Desktop position of the contact; it stops following the pointer at release. */
  x: number;
  y: number;
  /** The app has seen at least one frame with the contact down. */
  delivered: boolean;
  released: boolean;
  cancelled: boolean;
}

export async function mountStudioDesktop(options: MountStudioDesktopOptions): Promise<StudioDesktop> {
  const log = options.onLog ?? (() => {});
  const base = new URL(options.baseUrl.endsWith("/") ? options.baseUrl : `${options.baseUrl}/`, location.href);
  const response = await fetch(new URL("system.plan.json", base));
  if (!response.ok) throw new Error(`no system plan at ${base.href} (${response.status})`);
  const plan = (await response.json()) as SystemPlan;
  validatePlan(plan);

  const wasmUrl = new URL("runtime/pocketjs.wasm", base).href;
  const instanceUrl = new URL("runtime/app-instance.html", base).href;
  const artifact = (entry: SystemPackage, extension: string) =>
    new URL(`dist/${entry.plan.app.output}.${extension}`, base).href;

  const [width, height] = plan.systemUI.plan.viewport.logical;
  const size = [width, height] as const;
  const canvas = options.canvas ?? document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context2d = canvas.getContext("2d");
  if (!context2d) throw new Error("the desktop canvas has no 2D context");
  const context: CanvasRenderingContext2D = context2d;
  const image = context.createImageData(width, height);

  // Surface handles are one-based positions in the installed application list.
  const catalog = new Map<number, SystemPackage>();
  const surfaces: Record<string, number> = {};
  plan.applications.forEach((entry, index) => {
    catalog.set(index + 1, entry);
    surfaces[entry.package] = index + 1;
  });

  const shellRealm = await createRealm(instanceUrl, {
    packageId: plan.systemUI.package,
    wasmUrl,
    bundleUrl: artifact(plan.systemUI, "js"),
    pakUrl: artifact(plan.systemUI, "pak"),
    viewport: size,
    rasterDensity: plan.systemUI.plan.viewport.rasterDensity,
    companions: plan.systemUI.plan.companions,
    surfaces,
  });
  const shell = shellRealm.api;
  const surfaceNodes = observeSurfaceNodes(shellRealm);
  const send = (line: ShellLine) => shell.sendService(line);
  const hello = () => send({ t: "hello", w: size[0], h: size[1], epoch: Date.now() });

  hello();
  /** The active theme: the one asked for at boot, then whatever the System UI reports. */
  let theme: DesktopTheme = options.theme ?? readStoredTheme(themeStore()) ?? DEFAULT_THEME;
  send({ t: "theme", id: theme });
  /** The language the System UI was last told; English until a state line names another. */
  let lang: Lang = "en";
  // The System UI opens its welcome note focused; the close chord removes it.
  if (!options.welcome) send({ t: "key", k: "w", cmd: true, sh: false, alt: false, ctl: false });

  const children = new Map<number, Child>();
  const intentListeners = new Set<(intent: StudioIntentLine) => void>();
  const frameListeners = new Set<() => void>();
  const cursorListeners = new Set<(cursor: string) => void>();
  const quitListeners = new Set<() => void>();
  const themeListeners = new Set<(theme: DesktopTheme) => void>();
  const subscribe = <T>(set: Set<T>, listener: T) => {
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  };
  /**
   * Listener calls wait here until the step that produced them has finished,
   * so a listener may publish, change the rate or stop the desktop.
   */
  const outbox: Array<() => void> = [];
  function flush(): void {
    for (const call of outbox.splice(0)) {
      try {
        call();
      } catch (error) {
        log(`a desktop listener threw: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  }

  let stopped = false;
  let rate: DesktopRate = "full";
  let timer: { kind: "raf" | "timeout"; id: number } | null = null;
  let last = performance.now();
  let accumulator = 0;
  /** Wall time the scheduler skipped; the taskbar clock is re-anchored past one second. */
  let dropped = 0;

  let frames: SurfaceFrame[] = [];
  let focusedHandle: number | null = null;
  let shellHash: bigint | null = null;
  let surfacesChanged = true;

  let heldButtons = 0;
  /** Bits pressed since the last step, so a key tap shorter than a frame still lands. */
  let tappedButtons = 0;
  let pointerX = 0;
  let pointerY = 0;
  let primaryDown = false;
  let press: Press | null = null;
  let latestState: StudioStateLine | null = null;
  /** Apps asked for while paused, oldest first. */
  let pendingLaunches: string[] = [];
  /** Theme asked for while paused. */
  let pendingTheme: DesktopTheme | null = null;
  /** What launch() asked each app to show, by package id, until the app's window closes. */
  const shows = new Map<string, ShowRequest>();
  let showCount = 0;

  /** Ask the System UI for a theme; its report makes the change take effect here. */
  function selectTheme(next: DesktopTheme): void {
    if (rate === "paused") pendingTheme = next;
    else send({ t: "theme", id: next });
  }

  function openChild(handle: number): void {
    const entry = catalog.get(handle);
    if (!entry) {
      log(`unknown surface handle ${handle}`);
      return;
    }
    const child: Child = {
      entry,
      realm: null,
      closed: false,
      failed: false,
      retryAt: Infinity,
      stale: true,
      hash: null,
      contactHit: null,
    };
    children.set(handle, child);
    createRealm(instanceUrl, {
      packageId: entry.package,
      wasmUrl,
      bundleUrl: artifact(entry, "js"),
      pakUrl: artifact(entry, "pak"),
      viewport: entry.plan.viewport.logical,
      rasterDensity: entry.plan.viewport.rasterDensity,
      companions: [STUDIO_SERVICE],
    }).then(
      (realm) => {
        if (child.closed || stopped) {
          realm.api.dispose();
          realm.iframe.remove();
          return;
        }
        child.realm = realm;
        log(`started ${entry.package}`);
      },
      (error: unknown) => {
        child.failed = true;
        child.retryAt = performance.now() + START_RETRY_MS;
        log(`${entry.package} failed to start: ${error instanceof Error ? error.message : String(error)}`);
      },
    );
  }

  function closeChild(handle: number): void {
    const child = children.get(handle);
    if (!child) return;
    children.delete(handle);
    shows.delete(child.entry.package);
    child.closed = true;
    shell.freeSurface(handle);
    surfacesChanged = true;
    if (press?.handle === handle) press = null;
    if (child.realm) {
      child.realm.api.dispose();
      child.realm.iframe.remove();
      log(`closed ${child.entry.package}`);
    }
  }

  /** The app surface a desktop point lands on, or null when shell chrome covers it. */
  function surfaceAt(x: number, y: number): SurfaceFrame | null {
    const frame = frameAt(frames, x, y);
    if (!frame) return null;
    const node = surfaceNodes?.get(frame.handle);
    if (node !== undefined && shell.hitTestBounds(x, y) !== node) return null;
    return frame;
  }

  function stepChild(handle: number, child: Child, frame: SurfaceFrame | null, render: boolean): void {
    const realm = child.realm;
    if (!realm || child.failed) {
      // A press on a window whose app is not running has nothing to land on.
      if (press?.handle === handle) press = null;
      return;
    }
    const api = realm.api;
    try {
      if (child.stale && latestState !== null) {
        api.sendService(lineFor(latestState, theme, shows.get(child.entry.package)));
        child.stale = false;
      }
      let touches: number[] | undefined;
      let hits: number[] | undefined;
      if (press && press.handle === handle) {
        if (!frame || (!press.delivered && handle !== focusedHandle)) {
          press = null;
        } else if (press.cancelled) {
          touches = [packTouchCancel(0)];
          hits = [0];
          press = null;
        } else {
          const [x, y] = toClient(frame, api.viewport, press.x, press.y);
          // The hit fact resolves once, against the frame the user pressed on.
          if (!press.delivered) child.contactHit = api.hitTestBounds(x, y);
          touches = [packTouchWide(0, x, y)];
          hits = [child.contactHit ?? 0];
          press.delivered = true;
          if (press.released) press = null;
        }
      }
      const buttons = handle === focusedHandle ? heldButtons | tappedButtons : 0;
      api.step(buttons, touches, hits, touches ? [0] : undefined);
      for (const line of api.drainService()) {
        const intent = parseIntent(line);
        if (!intent) continue;
        if (intent.t === "theme") selectTheme(intent.theme);
        for (const listener of intentListeners) outbox.push(() => listener(intent));
      }
      if (render && frame) {
        const hash = api.drawHash();
        // 0n means the wasm build has no draw hash; render every time then.
        if (child.hash === null || hash === 0n || hash !== child.hash) {
          const [w, h] = api.viewport;
          if (shell.uploadSurface(handle, api.render(), w, h) < 0) {
            throw new Error("the shell compositor refused the surface");
          }
          if (child.hash === null) log(`composited ${child.entry.package} (${w}x${h})`);
          child.hash = hash;
          surfacesChanged = true;
        }
      }
    } catch (error) {
      child.failed = true;
      log(`${child.entry.package} stopped: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function handleShellLines(): void {
    for (const line of shell.drainService()) {
      let message: { t?: unknown; k?: unknown; text?: unknown; id?: unknown };
      try {
        message = JSON.parse(line);
      } catch {
        continue;
      }
      if (!message || typeof message !== "object") continue;
      const reported = reportedTheme(message);
      if (reported) {
        if (reported !== theme) {
          theme = reported;
          storeTheme(themeStore(), reported);
          // The apps draw in the theme the state line names.
          for (const child of children.values()) child.stale = true;
          for (const listener of themeListeners) outbox.push(() => listener(reported));
          log(`theme ${reported}`);
        }
      } else if (message.t === "cursor" && typeof message.k === "string") {
        const cursor = CURSORS[message.k] ?? "default";
        for (const listener of cursorListeners) outbox.push(() => listener(cursor));
      } else if (message.t === "copy" && typeof message.text === "string") {
        navigator.clipboard?.writeText(message.text).catch(() => {});
      } else if (message.t === "paste-req") {
        paste();
      } else if (message.t === "quit") {
        for (const listener of quitListeners) outbox.push(listener);
      }
    }
  }

  function paste(): void {
    navigator.clipboard?.readText().then(
      (text) => {
        if (text && !stopped) send({ t: "paste", text });
      },
      () => {},
    );
  }

  /** One fixed step of the System UI and every scheduled app. */
  function step(render: boolean): void {
    shell.step(0);

    const live = new Set(shell.bindings().map((binding) => binding.handle));
    const now = performance.now();
    for (const [handle, child] of [...children]) {
      if (!live.has(handle)) closeChild(handle);
      else if (child.failed && !child.realm && now >= child.retryAt) children.delete(handle);
    }
    for (const handle of live) if (!children.has(handle)) openChild(handle);

    frames = shell.frames();
    const byHandle = new Map<number, SurfaceFrame>();
    for (const frame of frames) byHandle.set(frame.handle, frame);
    let focused: SurfaceFrame | null = null;
    for (const frame of frames) if (frame.focused) focused = frame;
    focusedHandle = focused?.handle ?? null;

    const background = plan.lifecycle.backgroundExecution === "continue";
    for (const [handle, child] of children) {
      const frame = byHandle.get(handle) ?? null;
      if (!frame && !background) {
        if (press?.handle === handle) press = null;
        continue;
      }
      stepChild(handle, child, frame, render);
    }
    if (press && !children.has(press.handle)) press = null;
    tappedButtons = 0;
    handleShellLines();
    flush();
  }

  function present(): void {
    const hash = shell.drawHash();
    if (!surfacesChanged && shellHash !== null && hash !== 0n && hash === shellHash) return;
    shellHash = hash;
    surfacesChanged = false;
    image.data.set(shell.renderComposited());
    context.putImageData(image, 0, 0);
    for (const listener of frameListeners) outbox.push(listener);
    flush();
  }

  function fail(error: unknown): void {
    log(`the desktop stopped: ${error instanceof Error ? (error.stack ?? error.message) : String(error)}`);
    cancel();
    stopped = true;
  }

  function tick(now: number): void {
    timer = null;
    if (stopped || rate === "paused") return;
    const elapsed = Math.max(0, now - last);
    last = now;
    accumulator += Math.min(CATCH_UP_MS, elapsed);
    dropped += Math.max(0, elapsed - CATCH_UP_MS);
    const limit = rate === "full" ? 4 : Math.ceil(CATCH_UP_MS / FRAME_MS);
    const due = Math.min(limit, Math.floor(accumulator / FRAME_MS));
    accumulator -= due * FRAME_MS;
    if (accumulator >= FRAME_MS) {
      dropped += accumulator - (accumulator % FRAME_MS);
      accumulator %= FRAME_MS;
    }
    // The System UI derives its clock from the hello epoch plus stepped time.
    if (dropped >= 1000) {
      dropped = 0;
      hello();
    }
    try {
      for (let i = 0; i < due && !stopped; i++) step(i === due - 1);
      if (due > 0 && !stopped) present();
    } catch (error) {
      fail(error);
      return;
    }
    schedule();
  }

  function schedule(): void {
    if (stopped || timer || rate === "paused") return;
    timer =
      rate === "full"
        ? { kind: "raf", id: requestAnimationFrame(tick) }
        : { kind: "timeout", id: window.setTimeout(() => tick(performance.now()), IDLE_INTERVAL_MS) };
  }

  function cancel(): void {
    if (!timer) return;
    if (timer.kind === "raf") cancelAnimationFrame(timer.id);
    else clearTimeout(timer.id);
    timer = null;
  }

  function clamp(value: number, max: number): number {
    return Number.isFinite(value) ? Math.max(0, Math.min(max - 1, value)) : 0;
  }

  /** Let go of every held button and of the pointer. A contact in flight is cancelled, not tapped. */
  function release(): void {
    heldButtons = 0;
    tappedButtons = 0;
    if (press) {
      if (press.delivered) press.cancelled = true;
      else press = null;
    }
    if (primaryDown && !stopped) {
      primaryDown = false;
      send({ t: "mouse", x: pointerX, y: pointerY, d: false, sh: false });
    }
  }

  step(true);
  present();
  log(`booted ${plan.system.title} with ${plan.applications.length} applications`);
  last = performance.now();
  schedule();

  return {
    canvas,
    size,
    pointer(x, y, down, button = 1, shift = false) {
      if (stopped || rate === "paused") return;
      pointerX = clamp(x, size[0]);
      pointerY = clamp(y, size[1]);
      if (button === 2) {
        send({ t: "mouse", x: pointerX, y: pointerY, d: down, b: 2, sh: shift });
        return;
      }
      if (down && !primaryDown) {
        // Resolved before the shell sees the press, against the frame on
        // screen: a click on a menu that covers an app must not reach the app.
        const frame = surfaceAt(pointerX, pointerY);
        press = frame
          ? { handle: frame.handle, x: pointerX, y: pointerY, delivered: false, released: false, cancelled: false }
          : null;
      } else if (press && !press.released) {
        press.x = pointerX;
        press.y = pointerY;
        // A press the app has not seen yet stays for one frame, so a click
        // shorter than a step still taps.
        if (!down) {
          if (press.delivered) press = null;
          else press.released = true;
        }
      }
      primaryDown = down;
      send({ t: "mouse", x: pointerX, y: pointerY, d: down, sh: shift });
    },
    wheel(deltaY) {
      if (stopped || rate === "paused" || !Number.isFinite(deltaY) || deltaY === 0) return;
      send({ t: "scroll", dy: deltaY });
      const frame = surfaceAt(pointerX, pointerY);
      const line: DeskLine = { t: "wheel", dy: deltaY };
      if (frame) children.get(frame.handle)?.realm?.api.sendService(line);
    },
    keyDown(event) {
      if (stopped || rate === "paused") return false;
      const action = routeKey(event, focusedHandle !== null);
      if (!action) return false;
      if (action.kind === "button") {
        heldButtons |= action.bit;
        tappedButtons |= action.bit;
      } else if (action.kind === "paste") {
        paste();
      } else {
        send(action.line);
      }
      return true;
    },
    keyUp(event) {
      const bit = KEY_BUTTONS[event.code];
      if (bit !== undefined) heldButtons &= ~bit;
    },
    blur: release,
    publish(line) {
      // A copy, so an app that starts later or redraws for a theme gets the
      // line as it was published, whatever the page did to its object since.
      latestState = JSON.parse(JSON.stringify(line)) as StudioStateLine;
      for (const child of children.values()) child.stale = true;
      // The apps read the language on the line; the System UI is told on a
      // line of its own, with the apps' titles in that language.
      const named = langOf((line as { lang?: unknown }).lang);
      if (named && named !== lang) {
        lang = named;
        send(langLine(lang));
      }
    },
    onIntent: (listener) => subscribe(intentListeners, listener),
    theme: () => theme,
    setTheme(next) {
      if (!stopped) selectTheme(next);
    },
    onTheme: (listener) => subscribe(themeListeners, listener),
    onFrame: (listener) => subscribe(frameListeners, listener),
    onCursor: (listener) => subscribe(cursorListeners, listener),
    onQuit: (listener) => subscribe(quitListeners, listener),
    windows() {
      return frames.flatMap((frame) => {
        const entry = catalog.get(frame.handle);
        return entry ? [{ package: entry.package, rect: frame.full, focused: frame.focused }] : [];
      });
    },
    launch(packageId, show) {
      if (stopped) return;
      if (!Object.hasOwn(surfaces, packageId)) {
        log(`launch: ${packageId} is not an installed app`);
        return;
      }
      if (show !== undefined) {
        shows.set(packageId, { n: ++showCount, section: show });
        const child = children.get(surfaces[packageId]!);
        if (child) child.stale = true;
      }
      if (rate === "paused") pendingLaunches = queueLaunch(pendingLaunches, packageId);
      else send({ t: "open", package: packageId });
    },
    setRate(next) {
      if (stopped || next === rate) return;
      const resumed = rate === "paused";
      // A paused desktop takes no input, so nothing may stay held across it.
      if (next === "paused") release();
      rate = next;
      cancel();
      if (resumed) {
        // Stepped time stood still while paused; give the clock a new epoch.
        last = performance.now();
        accumulator = 0;
        dropped = 0;
        hello();
        for (const packageId of pendingLaunches.splice(0)) send({ t: "open", package: packageId });
        if (pendingTheme) send({ t: "theme", id: pendingTheme });
        pendingTheme = null;
      }
      schedule();
    },
    stop() {
      if (stopped) return;
      cancel();
      stopped = true;
      for (const handle of [...children.keys()]) closeChild(handle);
      shell.dispose();
      shellRealm.iframe.remove();
      intentListeners.clear();
      frameListeners.clear();
      cursorListeners.clear();
      quitListeners.clear();
      themeListeners.clear();
    },
  };
}
